import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, KeyRound } from "lucide-react";

/**
 * Patient-side: redeem a Companion App lifetime-access code that an admin
 * issued to them. Single redemption per patient — once redeemed, the card
 * shows a "Lifetime access granted" success state and the input disappears.
 */
export default function CompanionAccessRedeemCard() {
  const [grant, setGrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const cardRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/companion-access/mine");
      setGrant(data?.grant || null);
    } catch {
      // silent — endpoint may not exist on older deployments
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // If the URL hash points at us (e.g. /profile#companion-access from the
  // AccessGate "Have a code?" link), scroll into view + briefly highlight.
  useEffect(() => {
    if (loading) return;
    if (window.location.hash !== "#companion-access") return;
    if (!cardRef.current) return;
    cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    cardRef.current.classList.add("ring-4", "ring-purple-400", "ring-offset-2");
    const t = setTimeout(() => {
      cardRef.current?.classList.remove("ring-4", "ring-purple-400", "ring-offset-2");
    }, 2200);
    return () => clearTimeout(t);
  }, [loading]);

  const submit = async () => {
    if (!code.trim()) {
      toast.error("Enter a code first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post("/companion-access/redeem", { code: code.trim() });
      if (data?.already_granted) {
        toast.info("You already have lifetime Companion App access");
      } else {
        toast.success("🎉 Companion App unlocked for life!");
      }
      setCode("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't redeem that code");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div
      id="companion-access"
      ref={cardRef}
      className="rounded-2xl border border-purple-200 dark:border-purple-900/40 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-5 transition-all duration-300"
      data-testid="companion-access-redeem-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Companion App access code
          </h3>
          {grant ? (
            <div className="mt-2" data-testid="companion-access-granted-state">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4" />
                Lifetime access granted
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-2">
                Redeemed <b>{new Date(grant.granted_at).toLocaleDateString()}</b> · Code <span className="font-mono text-purple-700 dark:text-purple-300">{grant.code}</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                You&apos;ll always have access to the full Companion App (all subscription tabs), even if your subscription pauses or ends.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                Have a code from our team? Redeem it here to unlock the <strong>full Companion App</strong> — food logging, macro rings,
                Move tab, AI wellness assistant (not medical advice), and every subscriber-only tab — for life. (The free tabs
                — Rewards, Body, and Shot Reminders — already work without a code.) Codes look like <span className="font-mono">COMP-XXXX-XXXX-XXXX</span>.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <div className="flex-1">
                  <Label className="sr-only">Code</Label>
                  <Input
                    placeholder="COMP-XXXX-XXXX-XXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                    className="font-mono"
                    data-testid="companion-access-code-input"
                  />
                </div>
                <Button
                  onClick={submit}
                  disabled={submitting || !code.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="companion-access-redeem-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  {submitting ? "Redeeming…" : "Redeem"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
